import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CALCULATE_TEST_DURATION, RECALCULATE_TEST_DURATION, SEASON_DURATION, TEST_DESIGN, TEST_DESIGN_INFO } from '../../utils/Constants';
import CustomRadioButton from '../../components/CustomRadioButton';
import { scheduleItems } from '../../utils/ABHelper';
import DatePicker from 'Components/DesignSystem/DatePicker/DatePicker';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ABContext } from '../../contexts/ABContext';
import { calculateABTestDuration } from '../../services/ABServices';
import { CalculateTestDurationPayload, TPayload } from '../../types/ab';
import { toast } from "Components/DesignSystem/Toast/useToast";
import { areObjectsIdentical } from 'Pages/GMS/ISR/Components/RuleComponents/helper';

type TestDesignSectionProps = { testId: string, onSave: () => Promise<boolean | undefined>; }

const TestDesignSection = ({ testId, onSave }: TestDesignSectionProps) => {
    const abContext = useContext(ABContext);
    const [loading, setLoading] = useState(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    useEffect(() => {
        if (abContext?.payload) {
            abContext.setPayload((prevPayload: TPayload) => ({
                ...prevPayload,
                metric: prevPayload.metric || 'Revenue',
                is_seasonal: prevPayload.shopping_cycle === 'Seasonal' ? true : (prevPayload.is_seasonal ?? false),
                season_end: prevPayload.season_end,
                season_start: prevPayload.season_start,
                shopping_cycle: prevPayload.shopping_cycle
            }));
        }
    }, []);

    const handleShoppingCycleChange = useCallback((selectedShoppingCycle: string | ((prevState: string) => string)) => {
        if (abContext?.setPayload) {
            abContext.setPayload((prevPayload: TPayload) => {
                const updatedShoppingCycle = typeof selectedShoppingCycle === 'function' ? selectedShoppingCycle(prevPayload.shopping_cycle || '') : selectedShoppingCycle;

                const updatedPayload = {
                    ...prevPayload, 
                    //shopping_cycle: updatedShoppingCycle === 'Seasonal' ? 'Daily' : updatedShoppingCycle,
                    shopping_cycle: updatedShoppingCycle,
                    is_seasonal: updatedShoppingCycle === 'Seasonal'
                };
                return updatedPayload;
            });
        }
    }, []);

    // const handleIsSeasonalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     event.persist();
    //     if (abContext?.payload) {
    //         abContext.setPayload((prevPayload: TPayload) => ({
    //             ...prevPayload,
    //             is_seasonal: event.target.checked
    //         }));
    //     }
    // };

    const handleSeasonStartDateChange = (date: Date | null) => {
        if (!date) { return; }
        if (abContext?.payload) {
            abContext.setPayload((prevPayload: TPayload) => ({
                ...prevPayload,
                season_start: date.toISOString()
            }));
        }
    }
    const handleSeasonEndDateChange = (date: Date | null) => {
        if (!date) { return; }
        if (abContext?.payload) {
            abContext.setPayload((prevPayload: TPayload) => ({
                ...prevPayload,
                season_end: date.toISOString()
            }));
        }
    }

    function formatDateForAPI(dateString: string): string {
        const date = new Date(dateString);

        return date.toISOString().replace('Z', '+00:00');
    }

    const scrollToSaveButton = () => {
        const section = document.querySelector('#save-button');
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const calculateTestDuration = async () => {
        try {
            setLoading(true); 
            let updatedPayload = abContext?.payload;
            if (!abContext?.payload?.shopping_cycle) { toast.error("Please select shopping cycle"); return; }
            const isSeasonal = abContext?.payload?.shopping_cycle === 'Seasonal';
            const metric = abContext?.payload?.metric == null ? 'Revenue' : abContext?.payload?.metric;
            
            if (!isSeasonal) {
                updatedPayload = {
                    ...updatedPayload,
                    metric: metric,
                    is_seasonal: false,
                    season_end: null,
                    season_start: null,
                };
                abContext.setPayload(updatedPayload);
            }

            if (isSeasonal && (abContext?.payload?.season_start == null || abContext?.payload?.season_end == null)) {
                toast.error("Please select season start and end date")
                return;
            }

            if (abContext?.payload?.applets == null || updatedPayload?.metric == null) {
                toast.error("Please save the test details to calculate the test duration")
                scrollToSaveButton();
                return;
            }

            const formattedStartDate = formatDateForAPI(updatedPayload?.season_start)
            const formattedEndDate = formatDateForAPI(updatedPayload?.season_end)

            
            let shoppingCycleForAPI = abContext?.payload?.shopping_cycle;
            
            if (shoppingCycleForAPI === 'Seasonal') {
                
            
                shoppingCycleForAPI = 'Daily';
            }
            
            const requestBody: CalculateTestDurationPayload = {
                shopping_cycle: shoppingCycleForAPI,
                is_seasonal: isSeasonal,
                ...(isSeasonal && {
                    season_start: formattedStartDate,
                    season_end: formattedEndDate
                })
            };
            
            if (!areObjectsIdentical(abContext?.payload, abContext?.payloadNetworkCopy)) {
                const saved = await onSave();
                if (!saved) return;
            }
            const response = await calculateABTestDuration(testId, requestBody);
            if (response?.done) {
                if (abContext?.payloadNetworkCopy) {
                    abContext?.setPayloadNetworkCopy((prevPayload: TPayload) => ({
                        ...prevPayload,
                        estimated_orders_per_day: response?.data?.estimated_orders_per_day,
                        estimated_sessions_per_day: response?.data?.estimated_sessions_per_day,
                        test_duration: response?.data?.test_duration,
                    }));
                }
                if (abContext?.payload) {
                    abContext?.setPayload((prevPayload: TPayload) => ({
                        ...prevPayload,
                        estimated_orders_per_day: response?.data?.estimated_orders_per_day,
                        estimated_sessions_per_day: response?.data?.estimated_sessions_per_day,
                        test_duration: response?.data?.test_duration,
                    }));
                }
                toast.success(response?.message ?? "Test duration calculated successfully!")
            } else {
                toast.error(response?.message)
            }
        } catch (error) {
            console.log("getModels ~ error:", error);
        } finally {
            setLoading(false);
        }
    }
    

    const seasonStartDate = abContext?.payload?.season_start ? new Date(abContext.payload.season_start) : null;
    const seasonEndDate = abContext?.payload?.season_end ? new Date(abContext.payload.season_end) : null;


    const showSeasonalDates = abContext?.payload?.shopping_cycle === 'Seasonal';

    return (
        <section className='tw-border-b tw-border-edge tw-pb-6'>
            <Typography variant="title-header" as="h2" className="tw-text-ink">
                {TEST_DESIGN}
            </Typography>
            <div className='tw-mt-4'>
                <Typography variant="title" as="p" className="tw-text-ink-tertiary tw-mb-2">
                    {TEST_DESIGN_INFO}
                </Typography>
                <CustomRadioButton
                    list={scheduleItems}
                    aria-label={TEST_DESIGN_INFO}
                    data-testid="detail-shopping-cycle-radio"
                    selectedItem={abContext?.payload?.shopping_cycle}
                    setSelectedItem={handleShoppingCycleChange}
                />
            </div>

            {showSeasonalDates && (
                <Typography variant="title" as="p" className="tw-text-ink-tertiary tw-mt-4">
                    {SEASON_DURATION}
                </Typography>
            )}
            {showSeasonalDates && <div className="tw-flex tw-gap-4 tw-mt-2">
                {/*
                  DS `DatePicker` applies `popperProps={{ strategy: 'fixed' }}`
                  UNCONDITIONALLY (§ 128), so the must-not-regress these two carried —
                  react-datepicker@9's `@floating-ui` default of `absolute`, which clips
                  the calendar inside a scrolling ancestor — is now structural rather
                  than a prop someone has to remember. `CustomInput` goes with them.

                  Note `disabled` is deliberately not passed: under 9.1.0 it makes
                  `isDayDisabled` short-circuit true for every cell BEFORE consulting
                  `minDate`. Unreachable here (the field is never disabled), and the DS
                  component now renders a read-only input with no calendar at all in
                  that case, but worth not reintroducing.
                */}
                <DatePicker
                    label="From"
                    labelSize="sm"
                    value={seasonStartDate}
                    onChange={handleSeasonStartDateChange}
                    dateFormat="dd/MMM/yyyy"
                    minDate={today}
                    data-testid="detail-season-start"
                />
                <DatePicker
                    label="To"
                    labelSize="sm"
                    value={seasonEndDate}
                    onChange={handleSeasonEndDateChange}
                    minDate={seasonStartDate ? new Date(seasonStartDate.getTime() + 24*60*60*1000) : today}
                    dateFormat="dd/MMM/yyyy"
                    data-testid="detail-season-end"
                />
            </div>}
            {/*
              The busy state was a hand-rolled `tw-animate-spin` SVG that ignored
              `prefers-reduced-motion` and had no `role`, so "Calculating..." reached a
              screen reader only if it happened to be reading that region. `Button`
              carries `aria-busy` via `disabled` and the label change is the announcement.
            */}
            <Button
                variant="secondary"
                className="tw-mt-4"
                data-testid="detail-testdesignsection-button-1"
                onClick={calculateTestDuration}
                disabled={loading}
            >
                {loading
                    ? 'Calculating...'
                    : (abContext?.payload?.estimated_orders_per_day ? RECALCULATE_TEST_DURATION : CALCULATE_TEST_DURATION)}
            </Button>
        </section>
    );
}

export default TestDesignSection;